import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext.jsx';
import { useProject } from '@/context/ProjectContext.jsx';
import { useNavigationGuard } from '@/context/NavigationGuard.jsx';
import { ProjectPickerModal } from '@/pages/ProjectPickerPage.jsx';
import { useStorytellers } from '@/hooks/useStorytellers.js';
import { billing, reports } from '@/services/api.js';
import { supabase } from '@/services/supabase.js';
// ── NAV ITEMS by role ──
const STORYTELLER_NAV = [
  { icon: '🎙', label: 'Record my story',   path: '/story' },
  { icon: '📗', label: 'Book Preview',       path: '/book' },
  { icon: '🎵', label: 'Setup Remy', path: '/story?setup=1', forceReload: true },
  { icon: '❓', label: 'Help',               path: '/help' },
];

const CAPTAIN_NAV = [
  { icon: '🏠', label: 'Storyboard',         path: '/storyboard' },
  { icon: '📚', label: 'Chapters',            path: '/chapters' },
  { icon: '📗', label: 'Book Preview',        path: '/book' },
  { icon: '✏️',  label: 'Story Studio',        path: '/story-studio', unreadKey: 'captain_chat_unread' },
  { icon: '❓', label: 'Help',               path: '/help' },
];

const CREW_NAV = [
  { icon: '🏠', label: 'Storyboard',         path: '/storyboard' },
  { icon: '📗', label: 'Book Preview',        path: '/book' },
  { icon: '❓', label: 'Help',               path: '/help' },
];

// ── WISHES-ONLY NAV ──
// Per LEGACY-AND-WISHES-DESIGN.md. When a project's template is 'wishes',
// none of the memoir-flavored nav items apply (no chapters, no book
// preview yet, no recording). The menu is just the four legacy/wishes
// surfaces plus Help.
const WISHES_NAV = [
  { icon: '✉', label: 'Final Wishes',       path: '/legacy/final-wishes' },
  { icon: '📋', label: 'Wishes Document',    path: '/legacy/wishes' },
  { icon: '💌', label: 'Letters & Recipients', path: '/legacy/letters' },
  { icon: '🤝', label: 'Trustees',           path: '/legacy/trustees' },
  { icon: '❓', label: 'Help',               path: '/help' },
];

// Wishes items appended to memoir nav for memoir_and_wishes projects.
// Includes the full set including the wishes-document editor.
const WISHES_EXTENSIONS_FULL = [
  { icon: '✉', label: 'Final Wishes',       path: '/legacy/final-wishes' },
  { icon: '📋', label: 'Wishes Document',    path: '/legacy/wishes' },
  { icon: '💌', label: 'Letters & Recipients', path: '/legacy/letters' },
  { icon: '🤝', label: 'Trustees',           path: '/legacy/trustees' },
];

// Wishes items appended to memoir nav for legacy (life story for later)
// projects. No Wishes Document — that's enabled only for wishes/both
// templates per TEMPLATE_GRANTS.
const WISHES_EXTENSIONS_LEGACY = [
  { icon: '✉', label: 'Final Wishes',       path: '/legacy/final-wishes' },
  { icon: '💌', label: 'Letters & Recipients', path: '/legacy/letters' },
  { icon: '🤝', label: 'Trustees',           path: '/legacy/trustees' },
];

// These appear below the divider line for both roles
const BOTTOM_NAV = [
  { icon: '👤', label: 'Account',            path: '/settings' },
];

// ── PROJECT SWITCHER PILL ──
// Per MULTI-ROLE-DESIGN.md. Shows "{Storyteller name} · {Role}" in the
// header; click expands a dropdown listing all the user's projects. Hides
// when the user has zero projects (they're in onboarding) or one (no need
// to choose). Lets them jump to the picker page for "Start a new memoir".
function ProjectSwitcherPill() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { projects, activeProject, switchProject } = useProject();
  const [open, setOpen] = useState(false);
  const [showPickerModal, setShowPickerModal] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  // Don't render when there's nothing to pick.
  if (!activeProject && projects.length <= 1) return null;

  function roleBadge(role) {
    if (role === 'captain') return 'Captain';
    if (role === 'storyteller') return 'Storyteller';
    return 'Crew';
  }

  async function pick(p) {
    setOpen(false);
    if (!activeProject || p.storytellerProfileId !== activeProject.storytellerProfileId) {
      // Bridge to the legacy useStorytellers system: it reads the active
      // storyteller from localStorage and profiles.active_storyteller_id.
      // Write the localStorage key here so a same-route reload picks it up
      // immediately; the backend /me/active-project call (inside
      // switchProject) writes the profile column.
      if (user && user.id) {
        try { localStorage.setItem('active_storyteller_' + user.id, p.storytellerProfileId); } catch {}
      }
      await switchProject(p.storytellerProfileId);
      // Hard reload so every mounted page (FamilyPage, StoryStudio,
      // BookPage, etc.) re-runs its useStorytellers and refetches the new
      // storyteller's chapters, photos, sessions. Matches the existing
      // SwitchStorytellerModal pattern. Cleaner than navigating because
      // some pages don't unmount on a same-route navigate.
      const target = p.role === 'storyteller' ? '/story' : '/storyboard';
      window.location.assign(target);
    }
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Switch project"
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          background: open ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: '100px', padding: '6px 12px',
          cursor: 'pointer', minHeight: '36px',
          fontFamily: 'var(--font-sans)', color: 'var(--cream)',
          fontSize: '12px', maxWidth: '220px',
        }}
      >
        {activeProject ? (
          <>
            <span style={{
              background: 'rgba(250,247,242,0.18)', padding: '2px 8px', borderRadius: '100px',
              fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600,
              flexShrink: 0,
            }}>
              {roleBadge(activeProject.role)}
            </span>
            <span style={{
              maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              for {activeProject.storytellerFirstName || activeProject.storytellerName}
            </span>
          </>
        ) : (
          <span>Pick a project</span>
        )}
        <span style={{ fontSize: '10px', opacity: 0.7 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          background: 'var(--warm-white)', border: '1px solid var(--tan)',
          borderRadius: '14px', boxShadow: '0 8px 32px rgba(46,32,25,0.18)',
          minWidth: '260px', zIndex: 250, overflow: 'hidden',
          fontFamily: 'var(--font-sans)',
        }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--tan)', fontSize: '11px', color: 'var(--text-hint)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Your projects
          </div>
          <div style={{ padding: '4px 0', maxHeight: '320px', overflowY: 'auto' }}>
            {projects.map(p => {
              const isCurrent = activeProject && p.storytellerProfileId === activeProject.storytellerProfileId;
              return (
                <button
                  key={p.storytellerProfileId}
                  onClick={() => pick(p)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 14px', background: isCurrent ? 'var(--cream)' : 'none',
                    border: 'none', cursor: 'pointer', textAlign: 'left',
                  }}
                  onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = 'var(--cream)'; }}
                  onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = 'none'; }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.storytellerName}
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--text-hint)', marginTop: '2px' }}>
                      {roleBadge(p.role)}
                    </div>
                  </div>
                  {isCurrent && <span style={{ fontSize: '11px', color: 'var(--green-sage)', fontWeight: 600 }}>✓</span>}
                </button>
              );
            })}
          </div>
          <div style={{ borderTop: '1px solid var(--tan)', padding: '4px 0' }}>
            <button
              onClick={() => { setOpen(false); setShowPickerModal(true); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '12px', color: 'var(--brown)', textAlign: 'left',
              }}
            >
              View all projects →
            </button>
          </div>
        </div>
      )}

      {/* Full picker as a modal — replaces the legacy SwitchStorytellerModal */}
      <ProjectPickerModal open={showPickerModal} onClose={() => setShowPickerModal(false)} />
    </div>
  );
}

export function SiteHeader({ back }) {
  const { profile, signOut } = useAuth();
  // Use the per-project role (Phase 5 of multi-role refactor). Falls back
  // to false until projects load, which is fine — captain-only UI just
  // doesn't render until the active project resolves.
  const { isCaptain, isStoryteller, isCrew, activeProject } = useProject();
  const navigate  = useNavigate();
  const location  = useLocation();
  const { checkGuard } = useNavigationGuard();

  // Run any active page guard before performing the navigation. If no guard
  // is registered, this is a straight pass-through.
  function safeNavigate(path, opts) {
    checkGuard(() => navigate(path, opts), { type: 'menu', target: path });
  }
  const [open, setOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(false);
  const [projectTier, setProjectTier] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const menuRef   = useRef(null);
  const storytellersHook = useStorytellers();

  // Check admin status (cached after first lookup)
  useEffect(() => {
    if (!profile?.id) { setIsAdmin(false); return; }
    supabase.from('profiles').select('is_admin').eq('id', profile.id).maybeSingle()
      .then(({ data }) => setIsAdmin(!!data?.is_admin))
      .catch(() => setIsAdmin(false));
  }, [profile?.id]);

  // Load project tier from captain's profile (for crew members who don't own the tier)
  useEffect(() => {
    // After the multi-role migration, the 'family' role is collapsed into
    // 'crew'. We only need this lookup for crew members on the active
    // project — captains and storytellers don't need to read someone
    // else's tier.
    if (!profile?.id) return;
    if (!isCrew) return;
    supabase.from('family_links')
      .select('invited_by')
      .eq('family_user_id', profile.id)
      .eq('status', 'active')
      .maybeSingle()
      .then(({ data }) => {
        if (!data?.invited_by) return;
        supabase.from('profiles').select('tier')
          .eq('id', data.invited_by).maybeSingle()
          .then(({ data: captainData }) => {
            if (captainData?.tier) setProjectTier(captainData.tier);
          });
      })
      .catch(() => {});
  }, [profile?.id]);

  // Check for unread captain chat messages
  useEffect(() => {
    if (!profile?.id || !isCaptain) return;
    // Two-step query: first get storyteller_profile_id, then check unread column
    // This avoids a 400 if the captain_chat_unread column doesn't exist yet
    supabase
      .from('family_links')
      .select('storyteller_profile_id')
      .eq('family_user_id', profile.id)
      .eq('status', 'active')
      .maybeSingle()
      .then(({ data }) => {
        if (!data?.storyteller_profile_id) return;
        return supabase
          .from('storyteller_profiles')
          .select('captain_chat_unread')
          .eq('id', data.storyteller_profile_id)
          .maybeSingle();
      })
      .then((res) => {
        if (res?.data?.captain_chat_unread) {
          setChatUnread(true);
        } else {
          setChatUnread(false);
        }
      })
      .catch(() => {});
  }, [profile?.id, isCaptain, location.pathname]);

  // Close menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  // isStoryteller now sourced from useProject() above.
  const initials = [
    profile?.first_name?.[0],
    profile?.last_name?.[0],
  ].filter(Boolean).join('').toUpperCase() || '?';

  // Template-aware menu. Wishes-only swaps out the memoir surface
  // entirely. Legacy (Life story for later) and memoir_and_wishes
  // keep the memoir surface AND append the relevant wishes items so
  // the user can cross-navigate between writing chapters and managing
  // trustees/letters/wishes from one menu.
  const projectTemplate = activeProject?.projectTemplate;
  const isWishesProject = projectTemplate === 'wishes';
  const isLegacyProject = projectTemplate === 'legacy';
  const isMemoirAndWishes = projectTemplate === 'memoir_and_wishes';

  const roleLabel = isWishesProject
    ? 'Final Wishes'
    : isStoryteller ? 'Storyteller' : isCaptain ? 'Story Captain' : 'Story Crew';

  const baseNav = isStoryteller ? STORYTELLER_NAV : isCaptain ? CAPTAIN_NAV : CREW_NAV;
  let navItems;
  if (isWishesProject) {
    navItems = WISHES_NAV;
  } else if (isMemoirAndWishes) {
    // Memoir items first, then wishes items, then Help at the bottom.
    // Drop Help out of baseNav so it stays at the end.
    const baseNoHelp = baseNav.filter(item => item.path !== '/help');
    navItems = [...baseNoHelp, ...WISHES_EXTENSIONS_FULL, { icon: '❓', label: 'Help', path: '/help' }];
  } else if (isLegacyProject) {
    const baseNoHelp = baseNav.filter(item => item.path !== '/help');
    navItems = [...baseNoHelp, ...WISHES_EXTENSIONS_LEGACY, { icon: '❓', label: 'Help', path: '/help' }];
  } else {
    navItems = baseNav;
  }

  async function handleSignOut() {
    setOpen(false);
    await signOut();
    navigate('/', { replace: true });
  }

  return (
    <>
      <header style={{
        background: 'var(--brown-ink)', height: '60px',
        display: 'flex', alignItems: 'center', padding: '0 20px',
        justifyContent: 'space-between', position: 'sticky', top: 0,
        zIndex: 200, flexShrink: 0,
      }}>
        {/* LOGO — stacks on mobile */}
        <Link to="/" className="site-logo" style={{
          fontFamily: 'var(--font-serif)', fontStyle: 'italic',
          fontSize: '19px', color: 'var(--cream)', textDecoration: 'none',
          whiteSpace: 'nowrap', lineHeight: 1.2, flexShrink: 0,
        }}>
          <span className="logo-full">Do I Have <span style={{ color: 'var(--brown-light)' }}>a Story.</span></span>
          <span className="logo-stacked" style={{ display: 'none', flexDirection: 'column' }}>
            <span>Do I Have</span>
            <span style={{ color: 'var(--brown-light)' }}>a Story.</span>
          </span>
        </Link>

        {/* RIGHT SIDE */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Project switcher pill — visible when user has 1+ projects */}
          {profile && <ProjectSwitcherPill />}

          {/* Back button on certain screens */}
          {back && (
            <button onClick={() => navigate(back)} style={{
              fontSize: '13px', color: 'rgba(250,247,242,0.55)',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px', fontFamily: 'var(--font-sans)',
            }}>
              ← Back
            </button>
          )}

          {/* Chat button — captain only */}
          {profile && isCaptain && (
            <button
              onClick={() => {
                window.dispatchEvent(new CustomEvent('open-captain-chat'));
                if (!location.pathname.startsWith('/story-studio')) {
                  navigate('/story-studio?tab=chat');
                }
              }}
              style={{
                borderRadius: '100px',
                background: 'var(--brown)', border: '2px solid var(--brown-light)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--cream)', fontSize: '11px', fontWeight: 600,
                fontFamily: 'var(--font-sans)',
                cursor: 'pointer', flexShrink: 0,
                position: 'relative',
                padding: '8px 14px',
                minHeight: '36px',
                whiteSpace: 'nowrap',
              }}
            >
              Remy
              {chatUnread && (
                <span style={{
                  position: 'absolute', top: '-2px', right: '-2px',
                  width: '10px', height: '10px', borderRadius: '50%',
                  background: 'var(--amber)', border: '2px solid var(--brown-ink)',
                }} />
              )}
            </button>
          )}

          {/* Avatar + hamburger — only when logged in */}
          {profile && (
            <div ref={menuRef} style={{ position: 'relative' }}>
              {/* Hamburger button */}
              <button
                onClick={() => setOpen(o => !o)}
                aria-label="Open menu"
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  background: open ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '100px', padding: '6px 12px 6px 8px',
                  cursor: 'pointer', transition: 'background 0.15s',
                  minHeight: '44px',
                }}
              >
                {/* Avatar circle */}
                <div style={{
                  width: '30px', height: '30px', borderRadius: '50%',
                  background: 'var(--brown)', border: '2px solid var(--brown-light)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--cream)', fontSize: '13px',
                  fontFamily: 'var(--font-serif)', fontStyle: 'italic',
                  flexShrink: 0,
                }}>
                  {initials}
                </div>
                {/* Hamburger lines */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{
                      width: i === 1 ? '14px' : '18px',
                      height: '2px',
                      background: 'rgba(250,247,242,0.7)',
                      borderRadius: '2px',
                      transition: 'width 0.2s',
                    }} />
                  ))}
                </div>
              </button>

              {/* DROPDOWN MENU */}
              {open && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                  background: 'var(--warm-white)',
                  border: '1px solid var(--tan)',
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px rgba(46,32,25,0.18)',
                  minWidth: '240px',
                  zIndex: 300,
                  overflow: 'hidden',
                  animation: 'fadeUp 0.2s ease forwards',
                }}>

                  {/* User info header */}
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--tan)',
                    background: 'var(--cream)',
                  }}>
                    <div style={{
                      fontSize: '15px', fontWeight: 500,
                      color: 'var(--text-primary)', marginBottom: '2px',
                    }}>
                      {profile.first_name || 'Welcome'}
                    </div>
                    <div style={{
                      fontSize: '12px', color: 'var(--text-hint)',
                      fontFamily: 'var(--font-sans)',
                      display: 'flex', alignItems: 'center', gap: '6px',
                    }}>
                      {/* Tier badge: meaningful when there are multiple
                          tiers to choose from (Story vs Heirloom for
                          memoir). For wishes-only projects there's just
                          one product, so the badge is noise next to the
                          'Final Wishes' role label. Hide it there. */}
                      {!isWishesProject && (
                        <span style={{
                          background: 'var(--amber-light)', color: '#8B5E1A',
                          borderRadius: '100px', padding: '1px 8px',
                          fontSize: '10px', fontWeight: 600,
                          textTransform: 'uppercase', letterSpacing: '0.06em',
                        }}>
                          {projectTier || profile.tier || 'Story'}
                        </span>
                      )}
                      {isStoryteller ? 'Storyteller' : roleLabel}
                    </div>
                  </div>

                  {/* Nav links */}
                  <div style={{ padding: '8px 0' }}>
                    {navItems.map(item => (
                      <button
                        key={item.path + item.label}
                        onClick={() => {
                          setOpen(false);
                          if (item.label === 'Record for Robert') {
                            safeNavigate('/family');
                            setTimeout(() => {
                              window.dispatchEvent(new CustomEvent('proxy-mode'));
                            }, 100);
                          } else if (item.forceReload) {
                            // Force re-render even if already on the same base path
                            window.dispatchEvent(new CustomEvent('setup-remy'));
                            if (!location.pathname.startsWith('/story')) {
                              safeNavigate(item.path);
                            }
                          } else {
                            safeNavigate(item.path);
                          }
                        }}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center',
                          gap: '12px', padding: '13px 20px',
                          background: item.highlight ? 'rgba(122,140,110,0.08)' : 'none',
                          border: 'none', cursor: 'pointer',
                          fontFamily: 'var(--font-sans)', fontSize: '15px',
                          color: item.highlight ? 'var(--green-sage)' : 'var(--text-primary)',
                          fontWeight: item.highlight ? 600 : 400,
                          textAlign: 'left',
                          transition: 'background 0.12s',
                          minHeight: '48px',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = item.highlight ? 'rgba(122,140,110,0.14)' : 'var(--cream)'}
                        onMouseLeave={e => e.currentTarget.style.background = item.highlight ? 'rgba(122,140,110,0.08)' : 'none'}
                      >
                        <span style={{ fontSize: '18px', width: '24px', textAlign: 'center' }}>
                          {item.icon}
                        </span>
                        {item.label}
                        {item.unreadKey && chatUnread && (
                          <span style={{
                            width: '8px', height: '8px', borderRadius: '50%',
                            background: 'var(--amber)', display: 'inline-block',
                            marginLeft: '4px', flexShrink: 0,
                          }} />
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Switch Storyteller was here. Removed: the project pill
                      in the header now provides the same functionality
                      (per-project switching), and the pill's "View all
                      projects" link opens the full picker modal. */}

                  {/* Admin link — only for admins */}
                  {isAdmin && (
                    <>
                      <div style={{ height: '1px', background: 'var(--tan)', margin: '0 16px' }} />
                      <div style={{ padding: '8px 0' }}>
                        <button
                          onClick={() => { setOpen(false); navigate('/admin'); }}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center',
                            gap: '12px', padding: '13px 20px',
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontFamily: 'var(--font-sans)', fontSize: '15px',
                            color: 'var(--text-primary)', textAlign: 'left',
                            transition: 'background 0.12s', minHeight: '48px',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        >
                          <span style={{ fontSize: '18px', width: '24px', textAlign: 'center' }}>🛠️</span>
                          Admin Dashboard
                        </button>
                      </div>
                    </>
                  )}

                  {/* Divider */}
                  <div style={{ height: '1px', background: 'var(--tan)', margin: '0 16px' }} />

                  {/* Bottom nav items (Profiles) + Sign out */}
                  <div style={{ padding: '8px 0 4px' }}>
                    {BOTTOM_NAV.map(item => (
                      <button
                        key={item.path}
                        onClick={() => { setOpen(false); safeNavigate(item.path); }}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center',
                          gap: '12px', padding: '13px 20px',
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontFamily: 'var(--font-sans)', fontSize: '15px',
                          color: 'var(--text-primary)', textAlign: 'left',
                          minHeight: '48px', transition: 'background 0.12s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <span style={{ fontSize: '18px', width: '24px', textAlign: 'center' }}>{item.icon}</span>
                        {item.label}
                      </button>
                    ))}
                    <button
                      onClick={handleSignOut}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center',
                        gap: '12px', padding: '13px 20px',
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontFamily: 'var(--font-sans)', fontSize: '15px',
                        color: 'var(--red-soft)', textAlign: 'left',
                        minHeight: '48px',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--red-light)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >
                      <span style={{ fontSize: '18px', width: '24px', textAlign: 'center' }}>🚪</span>
                      Sign out
                    </button>
                  </div>

                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Backdrop — tapping outside closes menu on mobile */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 190,
            background: 'transparent',
          }}
        />
      )}

      {/* SwitchStorytellerModal was here. Removed: the ProjectPickerModal
          opened from the header pill replaces it. */}
    </>
  );
}

// ── Switch Storyteller Modal ──
export function SwitchStorytellerModal({ storytellers, activeId, onSwitch, onAddNew, onClose, title, subtitle, hideClose }) {
  return (
    <div onClick={hideClose ? undefined : onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(46,32,25,0.5)', zIndex: 400,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--warm-white)', borderRadius: '20px', padding: '28px 24px',
        width: '440px', maxWidth: '100%', maxHeight: '85vh', overflowY: 'auto',
      }}>
        <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '22px', marginBottom: '6px' }}>
          {title || (<><em>Switch</em> storyteller</>)}
        </h3>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: 1.6 }}>
          {subtitle || 'Choose which storyteller you want to view and edit. The whole site will refresh to show their content.'}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
          {storytellers.map(s => {
            const isActive = s.id === activeId;
            const name = [s.first_name, s.last_name].filter(Boolean).join(' ') || 'Unnamed storyteller';
            const volumeNum = s.volume_number || 1;
            const isVolume = volumeNum > 1 || s.parent_storyteller_id;
            return (
              <button key={s.id} onClick={() => isActive ? null : onSwitch(s.id)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                padding: '14px 16px', textAlign: 'left',
                background: isActive ? 'rgba(122,140,110,0.12)' : 'var(--cream)',
                border: '1.5px solid ' + (isActive ? 'var(--green-sage)' : 'var(--tan)'),
                borderRadius: '12px', cursor: isActive ? 'default' : 'pointer',
                fontFamily: 'var(--font-sans)',
              }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%',
                  background: 'var(--brown)', color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 600, fontSize: '15px', flexShrink: 0,
                }}>
                  {(s.first_name?.[0] || '?').toUpperCase() + (s.last_name?.[0] || '').toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    {name}
                    {isVolume && (
                      <span style={{ fontSize: '10px', fontWeight: 700, background: 'var(--gold)', color: 'var(--ink-deep)', padding: '2px 7px', borderRadius: '100px', letterSpacing: '0.05em' }}>
                        VOL {volumeNum}
                      </span>
                    )}
                  </p>
                  {s.email && <p style={{ fontSize: '12px', color: 'var(--text-hint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.email}</p>}
                </div>
                {isActive && (
                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--green-sage)', whiteSpace: 'nowrap' }}>
                    ✓ Active
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <button onClick={onAddNew} style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          background: 'var(--gold)', color: 'var(--ink-deep)', border: 'none',
          borderRadius: 'var(--radius-full)', padding: '14px',
          fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          fontFamily: 'var(--font-sans)', marginBottom: '10px',
        }}>
          + Set up another storyteller
        </button>
        {!hideClose && (
          <button onClick={onClose} style={{
            width: '100%', background: 'none', border: '1.5px solid var(--tan)',
            borderRadius: 'var(--radius-full)', padding: '12px',
            fontSize: '14px', color: 'var(--text-secondary)', cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
          }}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

// Full-page loader
export function PageLoader() {
  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--cream)',
      flexDirection: 'column', gap: '16px',
    }}>
      <p style={{
        fontFamily: 'var(--font-serif)', fontStyle: 'italic',
        fontSize: '22px', color: 'var(--brown-ink)',
      }}>
        Do I Have <em>a Story.</em>
      </p>
      <div style={{
        width: '24px', height: '24px',
        border: '2px solid var(--tan)',
        borderTopColor: 'var(--brown)',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }} />
    </div>
  );
}

// Requires auth guard
export function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

// ── FOOTER ──
export function SiteFooter() {
  const year = new Date().getFullYear();
  const [showReport, setShowReport] = useState(false);

  const linkStyle = {
    color: 'var(--text-hint)', fontSize: '13px', textDecoration: 'none',
    fontFamily: 'var(--font-sans)', transition: 'color 0.15s', cursor: 'pointer',
    background: 'none', border: 'none', padding: 0,
  };

  return (
    <footer className="site-footer" style={{
      background: 'var(--warm-white)', borderTop: '1px solid var(--tan)',
      padding: '40px 24px 32px', marginTop: 'auto',
    }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Top row */}
        <div className="footer-top" style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          gap: '32px', flexWrap: 'wrap', marginBottom: '32px',
        }}>

          {/* Brand */}
          <div className="footer-brand" style={{ maxWidth: '260px' }}>
            <p style={{
              fontFamily: 'var(--font-serif)', fontStyle: 'italic',
              fontSize: '20px', color: 'var(--brown-ink)', marginBottom: '8px',
            }}>
              Do I Have <span style={{ color: 'var(--brown-light)' }}>a Story.</span>
            </p>
            <p style={{ fontSize: '13px', color: 'var(--text-hint)', lineHeight: 1.65, fontFamily: 'var(--font-sans)' }}>
              Preserving the stories that matter most — one conversation at a time.
            </p>
          </div>

          {/* Nav links */}
          <div className="footer-nav" style={{ display: 'flex', gap: '48px', flexWrap: 'wrap' }}>
            <div className="footer-nav-group">
              <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '12px', fontFamily: 'var(--font-sans)' }}>
                Navigate
              </p>
              <div className="footer-nav-links" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { label: 'Storyboard', path: '/storyboard' },
                  { label: 'Chapters',         path: '/chapters' },
                  { label: 'Book preview',     path: '/book' },
                  { label: 'Story Studio',     path: '/story-studio' },
                  { label: 'Settings',         path: '/settings' },
                ].map(l => (
                  <Link key={l.path} to={l.path} style={linkStyle}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--brown)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-hint)'}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="footer-nav-group">
              <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '12px', fontFamily: 'var(--font-sans)' }}>
                Legal
              </p>
              <div className="footer-nav-links" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { label: 'Privacy policy',    href: '/privacy' },
                  { label: 'Terms of service',  href: '/terms' },
                ].map(l => (
                  <Link key={l.href} to={l.href} style={linkStyle}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--brown)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-hint)'}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="footer-nav-group">
              <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '12px', fontFamily: 'var(--font-sans)' }}>
                Contact
              </p>
              <div className="footer-nav-links" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <a href="mailto:hello@doihaveastory.com" style={linkStyle}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--brown)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text-hint)'}
                >
                  hello@doihaveastory.com
                </a>
                <button onClick={() => setShowReport(true)} style={{ ...linkStyle, textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--brown)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text-hint)'}
                >
                  🐞 Report issue / suggestion
                </button>
                <p style={{ ...linkStyle, cursor: 'default' }}>Cincinnati, Ohio</p>
              </div>
              <p className="footer-social-label" style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '20px 0 12px', fontFamily: 'var(--font-sans)' }}>
                Follow us
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                {[
                  { label: 'Facebook', href: 'https://facebook.com/doihaveastory', icon: 'f' },
                  { label: 'Instagram', href: 'https://instagram.com/doihaveastory', icon: '◻' },
                ].map(s => (
                  <a
                    key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                    title={s.label}
                    style={{
                      width: '34px', height: '34px', borderRadius: '8px',
                      background: 'var(--tan)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', color: 'var(--text-secondary)',
                      fontSize: '13px', fontWeight: 700, fontFamily: 'sans-serif',
                      textDecoration: 'none', transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--brown-light)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--tan)'}
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom row */}
        <div className="footer-bottom" style={{
          borderTop: '1px solid var(--tan)', paddingTop: '20px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: '10px',
        }}>
          <p style={{ fontSize: '12px', color: 'var(--text-hint)', fontFamily: 'var(--font-sans)' }}>
            © {year} Do I Have a Story. All rights reserved.
          </p>
          <p style={{ fontSize: '12px', color: 'var(--text-hint)', fontFamily: 'var(--font-sans)' }}>
            Your stories belong to you — always.
          </p>
        </div>

      </div>

      {showReport && <ReportIssueModal onClose={() => setShowReport(false)} />}
    </footer>
  );
}

// ── REPORT ISSUE / SUGGESTION MODAL ──
function ReportIssueModal({ onClose }) {
  const [kind, setKind] = useState('issue');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null); // { name, dataUrl }
  const [page, setPage] = useState(typeof window !== 'undefined' ? window.location.pathname + window.location.search : '');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  function onPickScreenshot(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Screenshot must be smaller than 5 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setScreenshot({ name: file.name, dataUrl: reader.result });
    reader.onerror = () => setError('Could not read the screenshot.');
    reader.readAsDataURL(file);
    setError('');
  }

  async function submit() {
    if (!description.trim()) { setError('Please describe what happened.'); return; }
    setSending(true);
    setError('');
    try {
      await reports.sendIssue({
        kind,
        page,
        description: description.trim(),
        screenshotDataUrl: screenshot?.dataUrl || null,
        userAgent: navigator.userAgent,
      });
      setSent(true);
    } catch (err) {
      setError('Could not send the report: ' + err.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(46,32,25,0.55)', zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--warm-white)', borderRadius: '20px', padding: '28px 24px',
        width: '480px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto',
      }}>
        {sent ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{ fontSize: '52px', marginBottom: '14px' }}>✅</div>
            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '22px', marginBottom: '8px' }}>
              <em>Thank you!</em>
            </h3>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '24px' }}>
              Your report was sent to the team. We'll take a look soon and reach out if we need more details.
            </p>
            <button onClick={onClose} style={{
              width: '100%', background: 'var(--brown-dark)', border: 'none',
              borderRadius: 'var(--radius-full)', padding: '12px',
              fontSize: '14px', fontWeight: 600, color: 'var(--cream)', cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
            }}>Close</button>
          </div>
        ) : (
          <>
            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '22px', marginBottom: '6px' }}>
              <em>Report</em> an issue or suggestion
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '18px', lineHeight: 1.6 }}>
              Help us improve. Tell us what happened and we'll take a look.
            </p>

            <label style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-hint)', display: 'block', marginBottom: '6px' }}>
              Type
            </label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
              {[
                { id: 'issue', label: '🐞 Issue / bug' },
                { id: 'suggestion', label: '💡 Suggestion' },
              ].map(k => {
                const selected = kind === k.id;
                return (
                  <button key={k.id} onClick={() => setKind(k.id)} style={{
                    flex: 1, padding: '10px',
                    background: selected ? 'rgba(139,111,78,0.12)' : 'var(--cream)',
                    border: '1.5px solid ' + (selected ? 'var(--brown)' : 'var(--tan)'),
                    borderRadius: '10px', cursor: 'pointer',
                    fontSize: '13px', fontWeight: 600,
                    color: selected ? 'var(--brown-dark)' : 'var(--text-secondary)',
                    fontFamily: 'var(--font-sans)',
                  }}>{k.label}</button>
                );
              })}
            </div>

            <label style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-hint)', display: 'block', marginBottom: '6px' }}>
              Page where it happened
            </label>
            <input value={page} onChange={e => setPage(e.target.value)}
              style={{ width: '100%', background: 'var(--cream)', border: '1.5px solid var(--tan)', borderRadius: '10px', padding: '11px 13px', fontSize: '13px', fontFamily: 'monospace', color: 'var(--text-primary)', outline: 'none', marginBottom: '14px', boxSizing: 'border-box' }}
            />

            <label style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-hint)', display: 'block', marginBottom: '6px' }}>
              What happened?
            </label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder={kind === 'suggestion' ? "Tell us your idea — what would make this better?" : "Describe what you were doing, what you expected, and what actually happened."}
              rows={6}
              style={{ width: '100%', background: 'var(--cream)', border: '1.5px solid var(--tan)', borderRadius: '10px', padding: '11px 13px', fontSize: '14px', fontFamily: 'var(--font-sans)', color: 'var(--text-primary)', outline: 'none', resize: 'vertical', lineHeight: 1.5, marginBottom: '14px', boxSizing: 'border-box' }}
            />

            <label style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-hint)', display: 'block', marginBottom: '6px' }}>
              Screenshot (optional)
            </label>
            <div style={{ marginBottom: '14px' }}>
              {screenshot ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--cream)', border: '1px solid var(--tan)', borderRadius: '10px', padding: '8px 12px' }}>
                  <span style={{ fontSize: '20px' }}>🖼️</span>
                  <span style={{ flex: 1, fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'var(--font-sans)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{screenshot.name}</span>
                  <button onClick={() => setScreenshot(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--red-soft)', fontFamily: 'var(--font-sans)' }}>Remove</button>
                </div>
              ) : (
                <label style={{ display: 'block', textAlign: 'center', background: 'var(--cream)', border: '1.5px dashed var(--tan-dark)', borderRadius: '10px', padding: '14px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-secondary)', fontFamily: 'var(--font-sans)' }}>
                  📷 Add a screenshot
                  <input type="file" accept="image/*" onChange={onPickScreenshot} style={{ display: 'none' }} />
                </label>
              )}
            </div>

            {error && (
              <div style={{ background: '#FCE8E2', border: '1px solid #D07A5C', borderRadius: '10px', padding: '10px 13px', marginBottom: '14px', fontSize: '13px', color: '#8B3A1C', fontFamily: 'var(--font-sans)' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={onClose} disabled={sending} style={{
                flex: 1, background: 'none', border: '1.5px solid var(--tan)',
                borderRadius: 'var(--radius-full)', padding: '12px',
                fontSize: '14px', color: 'var(--text-secondary)', cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
              }}>Cancel</button>
              <button onClick={submit} disabled={sending || !description.trim()} style={{
                flex: 2, background: 'var(--brown-dark)', border: 'none',
                borderRadius: 'var(--radius-full)', padding: '12px',
                fontSize: '14px', fontWeight: 600, color: 'var(--cream)',
                cursor: (sending || !description.trim()) ? 'default' : 'pointer',
                opacity: (sending || !description.trim()) ? 0.6 : 1,
                fontFamily: 'var(--font-sans)',
              }}>{sending ? 'Sending…' : 'Send report'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

